import cloneDeep from "lodash/cloneDeep";
import { defineStore } from "pinia";
import { ref } from "vue";
import { Comment, type Comments } from "@/model";
import { type SORT_TYPE, useAppStore } from "./app-store";
import CommentDao from "./comment-dao";
import { useFileStore } from "./file-store";

type UpdateMutation = { comment: Comment };

type AddPayload = { comments: Comment[]; messageId: string };
type CreatePayload = {
  comment: Comment;
  domainId?: string;
  topicId?: string;
  messageId?: string;
};
export type UpdatePayload = {
  commentId: string;
  messageId: string;
  param: Partial<Comment>;
};
type FetchPayload = { topicId: string; messageId?: string; sort?: SORT_TYPE };

export type PayloadRegisterSubscription = {
  domainId: string;
  topicId: string;
  messageId: string;
};

export const useCommentStore = defineStore("comments", () => {
  const comments = ref<Comments>({});

  // 投稿に紐付いたコメントを取得します
  const findMessageComments = (
    topicId: string,
    messageId: string,
  ): Comment[] => {
    if (comments.value[topicId] === undefined) {
      comments.value = { ...comments.value, [topicId]: {} };
    }

    if (comments.value[topicId][messageId] === undefined) {
      const messageCommentList: Comment[] = [];
      comments.value = {
        ...comments.value,
        [topicId]: {
          ...comments.value[topicId],
          [messageId]: messageCommentList,
        },
      };
    }
    return comments.value[topicId][messageId];
  };

  // --- getters ---
  /** 投稿IDからコメント一覧を取得する */
  const get = (topicId: string, messageId?: string): Comment[] | undefined => {
    const topicComments = comments.value[topicId];
    if (!topicComments) return undefined;
    return topicComments[messageId as string];
  };

  /** Topic内のコメント全取得 */
  const getByTopic = (
    topicId: string,
  ): { [messageId: string]: Comment[] | undefined } => {
    return comments.value[topicId];
  };

  // --- 内部更新 (旧 mutations 相当) ---
  const addMutation = (payload: { comments: Comment[] }): void => {
    payload.comments.forEach((comment) => {
      const msgComments = findMessageComments(
        comment.topicId,
        comment.messageId,
      );
      const index = msgComments.findIndex((c) => c.id === comment.id);
      if (index < 0) {
        msgComments.push(comment);
      } else {
        msgComments.splice(index, 1, comment);
      }
    });
  };

  const _updateMutation = (payload: UpdateMutation): void => {
    const comment = payload.comment;
    const msgComments = findMessageComments(comment.topicId, comment.messageId);
    const index = msgComments.findIndex((c) => c.id === comment.id);
    if (index > -1) {
      const target = msgComments[index];
      const updated = cloneDeep(target);
      updated.copyFrom(comment);
      msgComments.splice(index, 1, updated);
    }
  };

  // --- actions ---
  const add = async ({ comments: list }: AddPayload): Promise<void> => {
    if (list && 0 < list.length) {
      addMutation({ comments: list });
    }
  };

  /** コメントを新規作成する */
  const create = async ({
    comment,
    domainId,
    topicId,
    messageId,
  }: CreatePayload): Promise<void> => {
    const app = useAppStore();
    comment.setDomainAndTopicAndMessageIfNotSet(
      domainId || app.domainId,
      topicId || app.topicId,
      messageId || app.messageId,
    );
    // DBに保存
    const register = await CommentDao.create(comment);
    if (register) {
      const newComment = register.comment;
      addMutation({ comments: [newComment] });
      const thumbs = register.thumbs;
      const data = register.data;
      useFileStore().set({ thumb: thumbs, data: data });
    }
  };

  /** コメントを更新する */
  const update = async (envelope: UpdatePayload): Promise<void> => {
    const app = useAppStore();
    const commentId = envelope.commentId;
    const messageId = envelope.messageId;
    const domainId = app.domainId;
    const topicId = app.topicId;
    const param = envelope.param;

    const commentObject: Partial<Comment> = {
      ...param,
      id: commentId,
      domainId,
      topicId,
      messageId,
    };
    const comment = Comment.create(commentObject);

    const register = await CommentDao.update(comment);
    if (register) {
      const newComment = register.comment;
      addMutation({ comments: [newComment] });
      const thumbs = register.thumbs;
      const data = register.data;
      useFileStore().set({ thumb: thumbs, data: data });
    }
  };

  /** DBからコメントデータをロードして Store に保存する */
  const fetch = async ({
    topicId,
    messageId,
    sort,
  }: FetchPayload): Promise<void> => {
    const domainId = useAppStore().domainId;
    const results = await CommentDao.read(domainId, topicId, messageId, sort);
    if (results) {
      addMutation({ comments: results.comments });
    }
  };

  /** Subscriptionを登録する */
  const registerSubscription = async ({
    domainId,
    topicId,
    messageId,
  }: PayloadRegisterSubscription): Promise<void> => {
    await CommentDao.registerSubscrptions(domainId, topicId, messageId, {
      onCreate: (_domainId: string, topicId: string, messageId: string) => {
        fetch({ topicId, messageId });
      },
      onUpdate: (_domainId: string, topicId: string, messageId: string) => {
        fetch({ topicId, messageId });
      },
      onDelete: () => {
        // 注: 旧 Vuex 実装は存在しない 'delete' mutation を commit しており
        // 実質的に no-op だったため、挙動を踏襲して何もしない
      },
    });
  };

  /** Subscriptionを登録解除 */
  const removeSubscription = async (payload: {
    messageId: string;
  }): Promise<void> => {
    await CommentDao.closeSubscription(payload.messageId);
  };

  return {
    comments,
    get,
    getByTopic,
    add,
    create,
    update,
    fetch,
    registerSubscription,
    removeSubscription,
  };
});

export default useCommentStore;
